// Copyright (c) Hamilton Manufacturing Corp.
import React, { useEffect, useState } from "react";
import { QuestRoute } from "../../_common/api/QuestRoutes";
import { HamQuest } from "../../../App";
import Button from "../../_common/form/Button";
import Loading from "../../_common/Loading";
import styled from "styled-components";

const StyledButtonDiv = styled.div`
    display: flex;
    flex-direction: row;
    align-items: right;
    justify-content: right;
    margin: 1em 0;
`;

const StyledTextArea = styled.textarea`
    width: 90%;
    overflow-y: none;
    min-height: 100px;
    resize: vertical;
    padding: 8px;
    margin-top: 8px;
    border: 1px solid #adadad;
    border-radius: 4px;
    font: inherit;
    cursor: inherit;
    outline: inherit;
`;

const StyledErrorMessage = styled.p`
    font-size: small;
    font: inherit;
    color: red;
`;

const Survey = ({ onSuccess, ...props }) => {
    const heading = props.heading
        ? props.heading
        : "Please take a few moments to leave your feedback";
    const [questions, setQuestions] = useState([]);
    const [responses, setResponses] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        // Fetch the questions
        HamQuest.get(QuestRoute.survey.questions.leaving)
            .then((res) => {
                // An empty object was returned.
                if (Object.keys(res).length === 0) return onSuccess();
                setQuestions(res);
                setIsLoading(false);
            })
            .catch((ex) => {
                // The questions failed to load, continue on.
                console.log(ex.message);
                return onSuccess();
            });
    }, []);

    // Use setTimeout to briefly display thank you message
    useEffect(() => {
        if (isSubmitted) {
            setTimeout(() => {
                onSuccess();
            }, 1000);
        }
    }, [isSubmitted]);

    const handleInputChange = (questionId, value) => {
        setResponses((prev) => ({
            ...prev,
            [questionId]: value,
        }));
        // Clear error when user starts typing
        if (errors[questionId]) {
            setErrors((prev) => ({
                ...prev,
                [questionId]: "",
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;
        // Make sure each field has input
        questions.forEach((question) => {
            const response = responses[question.surveyQuestionId] || "";
            if (!response.trim()) {
                newErrors[question.surveyQuestionId] = "This field is required";
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            setIsLoading(true);
            setErrors("");

            try {
                // Format responses to match the required structure
                const formattedResponses = Object.entries(responses).map(
                    ([questionId, response]) => ({
                        QuestionId: parseInt(questionId),
                        CustomerResponse: response,
                    })
                );
                const body = JSON.stringify(formattedResponses);
                const response = await HamQuest.post(
                    QuestRoute.survey.submit,
                    body,
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );
                // clear the fields
                setResponses({});
                setIsSubmitted(true);
            } catch (err) {
                setErrors(
                    err.message ||
                        "An error occurred while submitting responses"
                );
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleClose = () => {
        // User does not want to do the survey, return.
        return onSuccess();
    };

    if (isSubmitted) {
        return <div>Thank you for your response.</div>;
    }

    return (
        <>
            {isLoading ? (
                <Loading />
            ) : (
                <div>
                    <h3>{heading}</h3>
                    <div>
                        {questions.map((question, index) => (
                            <div key={question.surveyQuestionId}>
                                {index > 0 && <hr />}
                                <div>
                                    <label
                                        htmlFor={`question${question.surveyQuestionId}`}
                                    >
                                        {question.question}
                                    </label>
                                </div>

                                <StyledTextArea
                                    value={
                                        responses[question.surveyQuestionId] ||
                                        ""
                                    }
                                    onChange={(e) =>
                                        handleInputChange(
                                            question.surveyQuestionId,
                                            e.target.value
                                        )
                                    }
                                />
                                {errors[question.surveyQuestionId] && (
                                    <StyledErrorMessage>
                                        {errors[question.surveyQuestionId]}
                                    </StyledErrorMessage>
                                )}
                            </div>
                        ))}
                    </div>
                    {
                        <StyledButtonDiv>
                            <Button onClick={handleClose}>Close</Button>
                            <Button onClick={handleSubmit}>Submit</Button>
                        </StyledButtonDiv>
                    }
                </div>
            )}
        </>
    );
};

export default Survey;
